/**
 * One item row of the Solutions & Capabilities cards: an image or an icon, then the bold label
 * (smaller from `lg` up). Used by all four layouts under `../` (Ryerson and STS, desktop and
 * mobile), which wrap it in the item's link.
 */
import React from "react";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { useTheme } from "@ryerson/frontend.theme";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Typography } from "@ryerson/frontend.typography";
import { Media } from "@ryerson/frontend.next";
import { Icon, IconList } from "@ryerson/frontend.assets";
interface ListImageProps {
	/** Image drawn instead of the icon; its `alt` is `description`. */
	imageUrl?: string;
	/**
	 * Icon drawn when `imageUrl` is empty. An `undefined` value falls back to
	 * `solution-carbon-sheet-solutions`.
	 */
	icon: keyof IconList;
	/** The label shown beside the image or icon. */
	description?: string;
	/**
	 * Switches the row padding: 10 px below when `true`, 20 px above otherwise. Every caller
	 * passes `false`.
	 */
	mobile?: boolean;
}

const Section = styled(Flex)`
	${(props: { mobile: boolean }) => {
		const { mobile } = props;
		return css`
			padding-top: ${mobile ? 0 : "20px"};
			padding-bottom: ${mobile ? "10px" : 0};
		`;
	}}
`;

/** Renders one item row. */
const ListImage: React.FC<ListImageProps> = ({
	imageUrl = "",
	icon = "solution-carbon-sheet-solutions",
	description = "",
	mobile = false,
}) => {
	const { theme } = useTheme();

	return (
		<Section mobile={mobile}>
			<FlexItem>
				{imageUrl ? (
					<img src={imageUrl} alt={description} />
				) : (
					<Icon
						icon={icon}
						size="xl"
						css={css`
							margin-bottom: 10px;
						`}
					/>
				)}
			</FlexItem>
			<FlexItem
				alignSelf="center"
				css={css`
					padding-left: 20px;
				`}
			>
				<Media greaterThanOrEqual="lg">
					<Typography size="sm" weight="bold" color={theme.colors.primary.secondaryBrand}>
						{description}
					</Typography>
				</Media>
				<Media lessThan="lg">
					<Typography size="md" weight="bold" color={theme.colors.primary.secondaryBrand}>
						{description}
					</Typography>
				</Media>
			</FlexItem>
		</Section>
	);
};
export default ListImage;
